#asset_transcript_component {
  .transcript-header {
    @apply sticky -top-4 -mt-4 z-10 bg-neutral-0 dark:bg-neutral-900;

    .transcript-header__content {
      @apply flex items-center gap-4 pt-6 pb-1 overflow-hidden transition-all duration-300;
      max-height: 5rem;
      will-change: max-height, opacity, transform;
    }
  }
  .transcript-header--hidden {
    @apply pt-6;
  }
  .transcript-header--hidden .transcript-header__content {
    @apply pb-0 opacity-0 pointer-events-none;
    max-height: 0;
    padding-top: 0;
    transform: translateY(-0.5rem);
  }
  

  .timestamp {
    @apply absolute -top-[13px] text-2xs text-fg-muted left-1/2 transform -translate-x-1/2 invisible;
  }

  .section {
    @apply mb-4;
  }

  .bookmark {
    @apply flex items-baseline gap-2 my-3 text-lg font-semibold cursor-pointer text-fg-base;

    .bookmark-timestamp {
      @apply text-xs text-fg-muted ml-auto;
    }
  }

  .bookmark--selected {
    @apply !bg-primary-200/50 dark:!bg-primary-400/20 rounded inline;
  }

  &:not(.bookmarks-only) {
    .bookmark-timestamp {
      @apply hidden;
    }
  }

  .paragraph {
    @apply text-fg-base font-body break-words relative mb-4;

    &:hover {
      .timestamp {
        @apply visible;
      }
    }
  }

  .speaker-label {
    @apply text-xs font-semibold text-fg-muted mb-1 tracking-wide;
  }

  .sentence {
    @apply box-decoration-clone p-0.5 rounded cursor-pointer;
    @apply text-fg-muted hover:bg-neutral-300 dark:hover:bg-neutral-800;
    -webkit-touch-callout: none;

    &--playing {
      @apply !text-neutral-900 !bg-primary-300/50 hover:bg-primary-500/10 dark:!text-neutral-200 dark:!bg-primary-100/50 dark:hover:bg-neutral-700;
    }

    &--highlight {
      @apply !text-yellow-600 bg-amber-200/30 dark:!text-amber-200 dark:bg-yellow-500/30 hover:bg-yellow-500/30 dark:hover:bg-yellow-500/60;
    }

    &--selected {
      @apply !bg-primary-200/50 dark:!bg-primary-400/20;
    }

    &--editing {
      @apply bg-transparent text-neutral-900 hover:bg-transparent dark:text-neutral-200 dark:hover:bg-transparent;
    }
  }

  &.search-active {
    .timestamp {
      @apply visible;
    }
  }

  &.highlights-only {

    /* Hide sections that don't have any highlights */
    .section:not(:has(.sentence--highlight)) {
      @apply hidden;
    }

    /* Hide paragraphs without highlights — pre/post context comes from the same paragraph */
    .paragraph:not(:has(.sentence--highlight)) {
      @apply hidden;
    }

    /* Always show paragraph timestamps so the gap has a time anchor */
    .timestamp {
      @apply visible;
    }

    /* Hide all non-highlighted sentences by default */
    .sentence--normal {
      display: none;
    }

    /* Show highlighted sentences */
    .sentence--highlight {
      display: inline;
    }

    /* Show normal sentence immediately before a highlight */
    .sentence--normal:has(+ .sentence--highlight) {
      display: inline;
    }

    /* Show normal sentence immediately after a highlight */
    .sentence--highlight+.sentence--normal {
      display: inline;
    }

    /* Show empty state when no highlights exist */
    &:not(:has(.sentence--highlight)) .highlights-only-empty-state {
      @apply block;
    }
  }

  /* Break marker — inserted by JS in highlights-only mode where un-highlighted
     content has been collapsed between two visible chunks. */
  .transcript-break {
    @apply my-6;
  }
  .transcript-break__rule {
    @apply flex items-center gap-2.5 text-fg-muted;
  }
  .transcript-break__line {
    @apply flex-1 h-px bg-border-base;
  }
  .transcript-break__zigzag {
    @apply block;
    overflow: visible;
  }
  /* Match the visual style of the paragraph .timestamp so every break has the same
     time anchor underneath, even for intra-paragraph breaks where the paragraph's
     own timestamp is above the chunk above the break. */
  .transcript-break__time {
    @apply text-center text-2xs text-fg-muted mt-2;
  }
  /* Avoid duplicate when the next sibling is a paragraph with its own timestamp. */
  &.highlights-only .transcript-break + .paragraph > .timestamp {
    @apply invisible;
  }

  &.bookmarks-only {

    /* Hide all sentences by default */
    .sentence--normal,
    .sentence--highlight {
      display: none;
    }

    /* Hide sections without bookmarks */
    .section:not(:has(.js-bookmark)) {
      @apply hidden;
    }

    /* Hide the normal paragraph timestamp */
    .paragraph>.timestamp {
      @apply hidden;
    }

    /* Hide the "Return to current position" button */
    .js-scroll-to-position-container {
      @apply hidden;
    }
  }

  .search-match {
    @apply bg-neutral-400 dark:bg-neutral-600 rounded px-1 -mx-1 inline box-decoration-clone;
  }

  /* Prevent horizontal overflow/scroll within the transcript area (both desktop
     and mobile). Nothing inside should ever cause horizontal scrolling.
     Use clip (not hidden): overflow-x:hidden forces overflow-y to compute to
     auto and creates a sticky containing block, which trapped the
     "Return to current position" overlay inside this node instead of the
     lesson tab / bottom-sheet scrollport. */
  .transcript-paragraphs-container {
    overflow-x: clip;
  }

  /* On touch devices, prevent native text selection in the transcript area.
     We handle sentence selection entirely via JS touch events, so native
     selection (and its iOS callout: Copy, Writing Tools, Look Up) is unwanted.
     touch-action: pan-y tells the browser to only handle vertical panning
     natively; horizontal drags are passed to our JS drag-selection handler
     instead of scrolling the transcript sideways. */
  @media (pointer: coarse) {
    .transcript-paragraphs-container {
      -webkit-user-select: none;
      user-select: none;
      touch-action: pan-y;
    }
  }

  /* Selection handles — native-like drag handles for extending selection on touch */
  .transcript-selection-handle {
    position: absolute;
    z-index: 15;
    touch-action: none;
    pointer-events: auto;
  }

  .transcript-selection-handle__line {
    @apply bg-primary-500;
    width: 2px;
    position: absolute;
    top: 0;
  }

  .transcript-selection-handle__grip {
    @apply bg-primary-500;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    position: absolute;
    left: -9px;
  }

  .transcript-selection-handle__grip::before {
    content: '';
    position: absolute;
    inset: -14px;
  }

  .transcript-selection-handle[data-handle-type="start"] .transcript-selection-handle__grip {
    top: -10px;
  }

  .transcript-selection-handle[data-handle-type="end"] .transcript-selection-handle__grip {
    bottom: -10px;
  }

  @media (pointer: fine) {
    .transcript-selection-handle {
      display: none;
    }
  }

  .tippy-svg-arrow {
    fill: white;

    &:is(.dark *) {
      fill: theme('colors.neutral.800');
    }
  }
}
